import React from 'react';

import { useTheme } from '../../contexts/ThemeContext';
import { WeightProgressData } from '../../hooks/useWeightProgress';

interface WeightGaugeV2Props {
  data: WeightProgressData | null;
  loading: boolean;
  error: string | null;
  /** When a single day is selected, show daily weight comparison */
  dayWeight?: number | null;
  dayWeightLastWeek?: number | null;
}

/**
 * Weight Gauge V2 - 240° sweep gauge visualization
 *
 * Design:
 * - Single-band ~240° arc from 8 o'clock to 4 o'clock
 * - Zero at 12 o'clock (top)
 * - Left side = weight gain (red), Right side = weight loss (green)
 * - Background: muted dark red/green, fill: bright vivid red/green
 * - Center shows weekly change value
 *
 * Same props as WeightProgressTile for drop-in replacement.
 */
export const WeightGaugeV2: React.FC<WeightGaugeV2Props> = ({ data, loading, error, dayWeight, dayWeightLastWeek }) => {
  const MAX_WEEKLY_LBS = 5;

  const weeklyChange = data?.weeklyChange ?? 0;
  const changePercent = Math.min(100, Math.max(-100, (weeklyChange / MAX_WEEKLY_LBS) * 100));

  // Format helpers
  const formatChange = (change: number | null): string => {
    if (change === null) return '--';
    const sign = change > 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}`;
  };

  // Subscribe to theme changes so gauge re-renders with correct muted colors
  useTheme();

  // Colors — muted backgrounds use theme-aware CSS variables
  const rootStyles = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
  const MUTED_RED = rootStyles?.getPropertyValue('--gauge-muted-red').trim() || '#7f1d1d';
  const MUTED_GREEN = rootStyles?.getPropertyValue('--gauge-muted-green').trim() || '#14532d';
  const BRIGHT_RED = '#ef4444';
  const BRIGHT_GREEN = '#22c55e';

  const getTrendColor = (change: number | null) => {
    if (change === null || Math.abs(change) < 0.1) return 'var(--text-secondary)';
    return change > 0 ? BRIGHT_RED : BRIGHT_GREEN;
  };

  const trendColor = getTrendColor(data?.weeklyChange ?? null);

  // SVG arc geometry
  // Angles in standard math: 0° = 3 o'clock, -90° = 12 o'clock
  const ARC_START = -210; // 8 o'clock
  const ARC_ZERO = -90;   // 12 o'clock (zero point)
  const ARC_END = 30;     // 4 o'clock
  const HALF_SWEEP = 120; // degrees per side

  const cx = 100, cy = 100, radius = 80;
  const bandWidth = 16;

  const polarToCartesian = (x: number, y: number, r: number, angle: number) => {
    const rad = (angle * Math.PI) / 180;
    return { x: x + r * Math.cos(rad), y: y + r * Math.sin(rad) };
  };

  const describeArc = (x: number, y: number, r: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(x, y, r, endAngle);
    const end = polarToCartesian(x, y, r, startAngle);
    const largeArc = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
    const sweep = endAngle > startAngle ? 0 : 1;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} ${sweep} ${end.x} ${end.y}`;
  };

  // Fill arc path based on weekly change
  let fillPath = '';
  let fillColor = '';
  const hasWeeklyData = data?.weeklyChange !== null;

  if (hasWeeklyData && Math.abs(changePercent) > 0) {
    if (changePercent > 0) {
      // Weight GAIN — bright red, fill from 12 o'clock toward 8 o'clock (left)
      const fillAngle = ARC_ZERO - (changePercent / 100) * HALF_SWEEP;
      fillPath = describeArc(cx, cy, radius, fillAngle, ARC_ZERO);
      fillColor = BRIGHT_RED;
    } else {
      // Weight LOSS — bright green, fill from 12 o'clock toward 4 o'clock (right)
      const fillAngle = ARC_ZERO + (Math.abs(changePercent) / 100) * HALF_SWEEP;
      fillPath = describeArc(cx, cy, radius, ARC_ZERO, fillAngle);
      fillColor = BRIGHT_GREEN;
    }
  }

  // Tick mark positions (1-lb increments)
  const ticks = [1, 2, 3, 4];
  const tickOuter = radius + bandWidth / 2 + 4;
  const tickInner = radius + bandWidth / 2 - 2;

  if (loading) {
    return (
      <div className="progress-card weight-gauge-v2">
        <div className="card-header">
          <h3>Weight Progress</h3>
        </div>
        <div className="card-content loading-state">
          <div className="loading-spinner" />
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="progress-card weight-gauge-v2">
        <div className="card-header">
          <h3>Weight Progress</h3>
        </div>
        <div className="card-content error-state">
          <p>{error}</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="progress-card weight-gauge-v2">
      <div className="card-header">
        <h3>Weight Progress</h3>
      </div>

      <div className="card-content">
        <div className="gauge-container">
          <svg viewBox="-5 0 210 150" className="gauge-svg">
            {/* Background track — left half (gain/red) */}
            <path
              d={describeArc(cx, cy, radius, ARC_START, ARC_ZERO)}
              fill="none"
              stroke={MUTED_RED}
              strokeWidth={bandWidth}
              strokeLinecap="round"
            />

            {/* Background track — right half (loss/green) */}
            <path
              d={describeArc(cx, cy, radius, ARC_ZERO, ARC_END)}
              fill="none"
              stroke={MUTED_GREEN}
              strokeWidth={bandWidth}
              strokeLinecap="round"
            />

            {/* Fill arc — bright color from zero toward the appropriate side */}
            {fillPath && (
              <path
                d={fillPath}
                fill="none"
                stroke={fillColor}
                strokeWidth={bandWidth}
                strokeLinecap="round"
              />
            )}

            {/* Zero mark at 12 o'clock */}
            <line
              x1="100" y1="12" x2="100" y2="26"
              stroke="var(--text-tertiary)"
              strokeWidth="2"
              strokeLinecap="round"
            />

            {/* Tick marks — left side (gain/red) */}
            {ticks.map(i => {
              const angle = ARC_ZERO - (i / MAX_WEEKLY_LBS) * HALF_SWEEP;
              const outer = polarToCartesian(cx, cy, tickOuter, angle);
              const inner = polarToCartesian(cx, cy, tickInner, angle);
              return (
                <line
                  key={`tick-gain-${i}`}
                  x1={outer.x} y1={outer.y}
                  x2={inner.x} y2={inner.y}
                  stroke={MUTED_RED}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  opacity="0.8"
                />
              );
            })}

            {/* Tick marks — right side (loss/green) */}
            {ticks.map(i => {
              const angle = ARC_ZERO + (i / MAX_WEEKLY_LBS) * HALF_SWEEP;
              const outer = polarToCartesian(cx, cy, tickOuter, angle);
              const inner = polarToCartesian(cx, cy, tickInner, angle);
              return (
                <line
                  key={`tick-loss-${i}`}
                  x1={outer.x} y1={outer.y}
                  x2={inner.x} y2={inner.y}
                  stroke={MUTED_GREEN}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  opacity="0.8"
                />
              );
            })}

            {/* Scale labels */}
            {(() => {
              const leftLabel = polarToCartesian(cx, cy, radius + 26, ARC_START);
              const rightLabel = polarToCartesian(cx, cy, radius + 26, ARC_END);
              return (
                <>
                  <text x={leftLabel.x} y={leftLabel.y + 4} textAnchor="middle" fill={BRIGHT_RED} fontSize="11" fontWeight="600">+5</text>
                  <text x="100" y="10" textAnchor="middle" fill="var(--text-tertiary)" fontSize="10" fontWeight="500">0</text>
                  <text x={rightLabel.x} y={rightLabel.y + 4} textAnchor="middle" fill={BRIGHT_GREEN} fontSize="11" fontWeight="600">-5</text>
                </>
              );
            })()}
          </svg>

          {/* Center display */}
          <div className="gauge-center">
            <span className="weekly-change" style={{ color: trendColor }}>
              {formatChange(data?.weeklyChange ?? null)}
            </span>
            <span className="change-unit">lbs this week</span>
          </div>
        </div>

        {/* Stats row */}
        <div className="stats-row">
          <div className="stat-item">
            <span className="stat-label">Avg Weight</span>
            <span className="stat-value">
              {data?.currentWeek.average ? data.currentWeek.average.toFixed(1) : '--'}
              {' / '}
              <span className="stat-prev-week">{data?.previousWeek.average ? data.previousWeek.average.toFixed(1) : '--'}</span>
            </span>
            <span className="stat-detail">this week / last week</span>
          </div>

          {dayWeight !== undefined && (
            <div className="stat-item">
              <span className="stat-label">Day Weight</span>
              <span className="stat-value">
                {dayWeight !== null ? dayWeight.toFixed(1) : '--'}
                {' / '}
                <span className="stat-prev-week">{dayWeightLastWeek != null ? dayWeightLastWeek.toFixed(1) : '--'}</span>
              </span>
              <span className="stat-detail">today / same day last wk</span>
            </div>
          )}

        </div>

      </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .weight-gauge-v2 .card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding-top: 4px;
  }

  .weight-gauge-v2 .card-header {
    border-bottom: none;
    padding: 0;
    text-align: center;
  }

  .weight-gauge-v2 .card-header h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .weight-gauge-v2 .gauge-container {
    position: relative;
    width: 100%;
    max-width: 220px;
  }

  .weight-gauge-v2 .gauge-svg {
    width: 100%;
    height: auto;
  }

  .weight-gauge-v2 .gauge-center {
    position: absolute;
    bottom: 42px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1;
  }

  .weight-gauge-v2 .weekly-change {
    font-size: 36px;
    font-weight: 700;
  }

  .weight-gauge-v2 .change-unit {
    font-size: 12px;
    color: var(--text-tertiary);
    margin-top: 4px;
  }

  .weight-gauge-v2 .stats-row {
    display: flex;
    justify-content: space-around;
    width: 100%;
    padding: 6px 0 0;
  }

  .weight-gauge-v2 .stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .weight-gauge-v2 .stat-label {
    font-size: 10px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .weight-gauge-v2 .stat-value {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .weight-gauge-v2 .stat-detail {
    font-size: 10px;
    color: var(--text-tertiary);
  }

  .weight-gauge-v2 .stat-prev-week {
    color: var(--text-secondary);
    font-weight: 400;
  }

  .weight-gauge-v2 .loading-state,
  .weight-gauge-v2 .error-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 150px;
    color: var(--text-tertiary);
  }

  .weight-gauge-v2 .loading-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--border-secondary);
    border-top-color: var(--accent-blue);
    border-radius: 50%;
    animation: wgv2-spin 1s linear infinite;
  }

  @keyframes wgv2-spin {
    to { transform: rotate(360deg); }
  }
`;

export default WeightGaugeV2;
